import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { LIKE } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import Button from "../../framework/Button";
import TextInput from "../../framework/TextInput";
import SelectInput from "../../framework/SelectInput";
import GlobalContext from "../../framework/GlobalContext";
import { isEmpty } from "../../utils/utils";
import Shortcut from "../../framework/Shortcut";
import { filterBanks } from "../../services/bank-services";
import { fetchBranches } from "../../services/branch-service";
import NumberInput from "../../framework/NumberInput";
const BankReportFilter = ({
  reportRef,
  onFilterShow,
  refreshData = false,
  applyFilterOnMount = true,
}) => {
  const [bankCode, setBankCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [city, setCity] = useState("");
  const [area, setArea] = useState("");
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [branchSuggestions, setBranchSuggestions] = useState([]);
  const apr_filter_bank_name_ref = useRef();
  const applyFilterRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);

  const [bankSuggestions, setBankSuggestions] = useState([]);
  const withAccountNoCap = ({ value }) => value.length <= 25;
  const [orderBy] = useState([{ fieldName: "bankName", orderType: "asc" }]);

  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      if (!isEmpty(bankCode)) {
        filters.push(createFilterParam("bankCode", bankCode, "string", LIKE));
      }
      if (!isEmpty(bankName)) {
        filters.push(createFilterParam("bankName", bankName, "string", LIKE));
      }
      if (!isEmpty(accountNo)) {
        filters.push(createFilterParam("accountNo", accountNo, "string", LIKE));
      }
      if (!isEmpty(ifsc)) {
        filters.push(createFilterParam("ifsc", ifsc, "string", LIKE));
      }
      if (!isEmpty(pinCode)) {
        filters.push(createFilterParam("pincode", pinCode, "string", LIKE));
      }
      if (!isEmpty(city)) {
        filters.push(createFilterParam("city", city, "string", LIKE));
      }
      if (!isEmpty(area)) {
        filters.push(createFilterParam("area", area, "string", LIKE));
      }
      if (!isEmpty(selectedBranches)) {
        const filterParam = createFilterParam(
          "branchCode",
          selectedBranches.label,
          "string",
          LIKE
        );
        filters.push(filterParam);
      }
      if (orderBy.length) {
        result.orderBy = orderBy;
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      reportRef.current?.applyFilters(result);
    },
    [
      bankCode,
      bankName,
      accountNo,
      ifsc,
      pinCode,
      city,
      area,
      selectedBranches,
      orderBy,
      reportRef,
    ]
  );

  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;

    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);
  const withPincodeCap = ({ value }) => value.length <= 6;
  const handleBranchInputChange = (event) => {
    const value = event.target.value || "";
    if (value.length > 2) {
      setBranchSuggestions([]);
      fetchBranches({ search: value }).then((resp) => {
        let updatedResp = resp.result.rows.map((branch) => ({
          id: branch._id,
          label: branch.code,
        }));
        setBranchSuggestions(updatedResp);
      });
    }
  };
  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="bank_filter_name"
            label="Bank Name"
            ref={apr_filter_bank_name_ref}
            options={bankSuggestions}
            getOptionLabel={(option) => option.label || option}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label || value === "";
            }}
            disableClearable={false}
            onFocus={() => setCurrentComponent("bank_filter_name")}
            onChange={(e, values) => {
              setBankName(values ? values.label : "");
            }}
            value={bankName}
            onInputChange={(e) => {
              if (!e) return;
              setBankName(e.target.value);
              setBankSuggestions([]);
              if (!e?.target?.value) setBankName(null);
              if (e?.target?.value?.length > 2) {
                filterBanks(`bankName=${e.target.value.trim()}`).then(
                  (resp) => {
                    let updatedResp = resp.rows.map((bank) => ({
                      id: bank.bankId,
                      label: bank.bankName,
                    }));
                    setBankSuggestions(updatedResp);
                  }
                );
              }
            }}
            markers={JSON.stringify({
              up: "bank_filter_name",
              down: "bank_filter_bank_code",
              right: "bank_filter_bank_code",
              left: "bank_filter_name",
            })}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <TextInput
            id="bank_filter_bank_code"
            label="Bank Code"
            value={bankCode}
            onChange={(e) => {
              const sanitizedBankCode = e.target.value.replace(
                /[^a-zA-Z0-9]/g,
                ""
              );
              setBankCode(sanitizedBankCode);
            }}
            onFocus={() => setCurrentComponent("bank_filter_bank_code")}
            inputProps={{
              "data-markers": JSON.stringify({
                down: "bank_filter_account_number",
                left: "bank_filter_name",
                up: "bank_filter_name",
                right: "bank_filter_account_number",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="bank_filter_account_number"
            label="A/C"
            value={accountNo}
            isAllowed={withAccountNoCap}
            allowNegative={false}
            decimalScale={0}
            onChange={(e) => setAccountNo(e.target.value)}
            onFocus={() => setCurrentComponent("bank_filter_account_number")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "bank_filter_bank_code",
                down: "bank_filter_ifsc",
                right: "bank_filter_ifsc",
                left: "bank_filter_bank_code",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <TextInput
            id="bank_filter_ifsc"
            label="IFSC"
            value={ifsc}
            onChange={(e) => {
              const ifsc = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
              setIfsc(ifsc);
            }}
            onFocus={() => setCurrentComponent("bank_filter_ifsc")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "bank_filter_account_number",
                down: "bank_filter_pin_code",
                right: "bank_filter_pin_code",
                left: "bank_filter_account_number",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="bank_filter_pin_code"
            label="PIN CODE"
            value={pinCode || ""}
            onChange={(e) => setPinCode(e.target.value)}
            onFocus={() => setCurrentComponent("bank_filter_pin_code")}
            isAllowed={withPincodeCap}
            decimalScale={0}
            allowNegative={false}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "bank_filter_ifsc",
                down: "bank_filter_city",
                right: "bank_filter_city",
                left: "bank_filter_ifsc",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <TextInput
            id="bank_filter_city"
            label="CITY"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onFocus={() => setCurrentComponent("bank_filter_city")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "bank_filter_pin_code",
                down: "bank_filter_area",
                right: "bank_filter_area",
                left: "bank_filter_pin_code",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <TextInput
            id="bank_filter_area"
            label="AREA"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            onFocus={() => setCurrentComponent("bank_filter_area")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "bank_filter_city",
                down: "bank_filter_branch",
                right: "bank_filter_branch",
                left: "bank_filter_city",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="bank_filter_branch"
            value={selectedBranches}
            options={branchSuggestions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label;
            }}
            onChange={(e, value) => {
              setSelectedBranches(value);
            }}
            disableClearable={false}
            onInputChange={handleBranchInputChange}
            label="Branches"
            disableCloseOnSelect={false}
            markers={JSON.stringify({
              up: "bank_filter_area",
              down: "bank_apply_filter_button",
              right: "bank_apply_filter_button",
              left: "bank_filter_area",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  const filterFooter = (
    <FilterGroup>
      <FilterItem>
        <Button
          id="bank_apply_filter_button"
          label="Apply"
          ref={applyFilterRef}
          endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
          onClick={() => applyFilterHandler(true)}
          markers={JSON.stringify({
            up: "bank_filter_branch",
            down: "bank_apply_filter_button",
            right: "bank_apply_filter_button",
            left: "bank_filter_branch",
          })}
        />
      </FilterItem>
    </FilterGroup>
  );

  useEffect(() => {
    if (onFilterShow) {
      apr_filter_bank_name_ref?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);

  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);

  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default BankReportFilter;
